import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Users,
  Search,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  Car,
  UserCheck,
  Calendar,
} from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import usePersistentFilters from '../../hooks/usePersistentFilters';
import BlockDriverModal from '../../components/BlockDriverModal';

const PendingDrivers = () => {
  const { hasPermission, user } = useAuth();

  const getIncompleteDetails = (driver) => {
    const missingFields = [];
    const vehicle = driver.vehicleId;

    if (!driver.name?.trim()) missingFields.push('Driver Name');
    if (!driver.phone?.trim()) missingFields.push('Phone Number');
    if (!driver.gender?.trim()) missingFields.push('Gender');
    if (!driver.dob) missingFields.push('Date of Birth');
    if (!driver.licenseNo?.trim()) missingFields.push('License Number');
    if (!driver.documents?.driverPhoto?.trim()) missingFields.push('Driver Photo');
    if (!driver.documents?.drivingLicense?.trim()) missingFields.push('Driving License Document');
    if (!driver.bankDetails?.UPIID?.trim()) missingFields.push('UPI ID');

    const hasAadhar = driver.aadharNo?.trim() || driver.documents?.aadhar?.trim();
    const hasPan = driver.panNo?.trim() || driver.documents?.panCard?.trim();
    if (!hasAadhar && !hasPan) {
      missingFields.push('Aadhar or PAN (at least one required)');
    }

    if (!vehicle) {
      missingFields.push('Vehicle Assignment');
    } else {
      if (!vehicle.regNo?.trim()) missingFields.push('Vehicle Registration Number');
      if (!vehicle.documents?.vehicleRcDoc?.trim()) missingFields.push('Vehicle RC Document');

      if (vehicle.isParivahanFailed === true) {
        if (!vehicle.documents?.permitDoc?.trim()) missingFields.push('Permit Document');
        if (!vehicle.documents?.insurance?.trim()) missingFields.push('Insurance Document');
      }
    }

    return missingFields;
  };

  const defaultFilters = {
    search: '',
    requireCompleteDocuments: 'all',
    dateFilter: 'all',
    from_date: '',
    to_date: '',
    regionalOffice: '',
    page: 1,
    limit: 10
  };

  const {
    filters,
    handleFilterChange,
    handlePageChange,
    clearFilters,
    updateFilters
  } = usePersistentFilters(defaultFilters, 'pending-drivers-filters');

  const [hasInitialized, setHasInitialized] = React.useState(false);

  React.useEffect(() => {
    if (!hasInitialized) {
      if (filters.search) {
        handleFilterChange('search', '');
      }
      setHasInitialized(true);
    }
  }, [filters.search, handleFilterChange, hasInitialized]);

  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [driverToApprove, setDriverToApprove] = useState(null);
  const [regionalOffices, setRegionalOffices] = useState([]);
  const [activeTooltip, setActiveTooltip] = useState(null);

  const handleMouseEnterTooltip = (e, missingFields) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const spaceAbove = rect.top;
    const placeAbove = spaceAbove > 220;
    setActiveTooltip({
      x: rect.left,
      y: placeAbove ? rect.top - 8 : rect.bottom + 8,
      placeAbove,
      missingFields
    });
  };

  const handleMouseLeaveTooltip = () => {
    setActiveTooltip(null);
  };

  // Fetch regional offices
  React.useEffect(() => {
    const fetchRegionalOffices = async () => {
      try {
        const response = await api.get('/regional-offices');
        const offices = response.data?.data?.offices || response.data?.data || [];
        setRegionalOffices(Array.isArray(offices) ? offices : []);
      } catch (error) {
        console.error('Failed to fetch regional offices:', error);
        setRegionalOffices([]);
      }
    };
    fetchRegionalOffices();
  }, []);

  const { data, isLoading, error, refetch } = useQuery(
    ['pending-drivers', filters],
    async () => {
      console.log('Fetching pending drivers with filters:', filters);
      const response = await api.get('/drivers/pending', { params: filters });
      console.log('API Response:', response);
      console.log('Drivers data:', response?.data?.data?.drivers);
      return response;
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: 0,
      cacheTime: 0
    }
  );

  const handleApproveDriver = (driverId, driverName) => {
    setDriverToApprove({ id: driverId, name: driverName });
    setShowApprovalModal(true);
  };

  const confirmApproval = async () => {
    if (!driverToApprove) return;

    try {
      const endpoint = (user?.role === 'ADMIN' || user?.role === 'admin')
        ? `/drivers/admin/${driverToApprove.id}/approve`
        : `/drivers/${driverToApprove.id}/approve`;
      await api.put(endpoint);
      toast.success('Driver approved successfully');
      refetch();
    } catch (error) {
      toast.error('Failed to approve driver');
    } finally {
      setShowApprovalModal(false);
      setDriverToApprove(null);
    }
  };

  const cancelApproval = () => {
    setShowApprovalModal(false);
    setDriverToApprove(null);
  };

  const handleRejectDriver = async (driverId, reason) => {
    try {
      await api.patch(`/drivers/${driverId}/approval`, {
        isApproved: false,
        reason: reason
      });
      toast.success('Driver rejected successfully');
      refetch();
    } catch (error) {
      toast.error('Failed to reject driver');
    }
  };

  const handleBlockDriver = async (blockData) => {
    try {
      await api.put(`/drivers/${blockData.driverId}/block`, blockData);
      toast.success(`Driver ${blockData.isBlocked ? 'blocked' : 'unblocked'} successfully`);
      refetch();
    } catch (error) {
      toast.error('Failed to update driver status');
    }
  };

  if (error) {
    console.error('Error loading pending drivers:', error);
    toast.error('Failed to load pending drivers');
  }

  const drivers = data?.data?.data?.drivers || [];
  const pagination = data?.data?.data?.pagination || {};

  console.log('Drivers to display:', drivers);
  console.log('Pagination:', pagination);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="page-title">Pending Driver Approvals</h1>
              <p className="page-subtitle">
                Review and approve new driver registrations
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Link to="/drivers" className="btn btn-outline">
                <Users className="h-4 w-4 mr-2" />
                All Drivers
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="card-title">Search & Filter</h3>
            <button
              onClick={clearFilters}
              className="btn btn-outline btn-sm"
            >
              Clear Search
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search */}
              <div>
                <label className="form-label">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name, phone, license..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="input pl-10"
                  />
                </div>
              </div>

              {/* Document Completion Filter */}
              <div>
                <label className="form-label">Document Status</label>
                <select
                  value={filters.requireCompleteDocuments}
                  onChange={(e) => handleFilterChange('requireCompleteDocuments', e.target.value)}
                  className="input"
                >
                  <option value="all">All Pending Drivers</option>
                  <option value="true">Complete Documents Only</option>
                  <option value="false">Incomplete Documents Only</option>
                </select>
              </div>

              {/* Regional Office Filter */}
              <div>
                <label className="form-label">Regional Office</label>
                <select
                  value={filters.regionalOffice}
                  onChange={(e) => handleFilterChange('regionalOffice', e.target.value)}
                  className="input"
                >
                  <option value="">All Offices</option>
                  {Array.isArray(regionalOffices) && regionalOffices.map((office) => (
                    <option key={office._id} value={office._id}>
                      {office.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Filter */}
              <div>
                <label className="form-label">Registration Date</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      updateFilters({
                        ...filters,
                        dateFilter: 'all',
                        from_date: '',
                        to_date: '',
                        page: 1
                      });
                    }}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${filters.dateFilter === 'all'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    All Time
                  </button>
                  <button
                    onClick={() => {
                      updateFilters({
                        ...filters,
                        dateFilter: 'today',
                        from_date: '',
                        to_date: '',
                        page: 1
                      });
                    }}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${filters.dateFilter === 'today'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    Today
                  </button>
                  <button
                    onClick={() => {
                      updateFilters({
                        ...filters,
                        dateFilter: 'yesterday',
                        from_date: '',
                        to_date: '',
                        page: 1
                      });
                    }}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${filters.dateFilter === 'yesterday'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    Yesterday
                  </button>
                  <button
                    onClick={() => handleFilterChange('dateFilter', 'custom')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${filters.dateFilter === 'custom'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    Custom
                  </button>
                </div>
              </div>
            </div>

            {/* Custom Date Selection Panel (rendered below grid) */}
            {filters.dateFilter === 'custom' && (
              <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50/75 border border-gray-200/50 rounded-xl animate-[fadeIn_0.2s_ease-out] max-w-md">
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <Calendar className="h-3 w-3 text-primary-500" />
                    From Date
                  </label>
                  <input
                    type="date"
                    value={filters.from_date || ''}
                    onChange={(e) => handleFilterChange('from_date', e.target.value)}
                    className="input w-full bg-white border border-gray-200 hover:border-gray-300 focus:border-primary-500 text-sm py-1.5 px-3 rounded-lg shadow-sm transition-all focus:ring-2 focus:ring-primary-500/20"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <Calendar className="h-3 w-3 text-primary-500" />
                    To Date
                  </label>
                  <input
                    type="date"
                    value={filters.to_date || ''}
                    onChange={(e) => handleFilterChange('to_date', e.target.value)}
                    className="input w-full bg-white border border-gray-200 hover:border-gray-300 focus:border-primary-500 text-sm py-1.5 px-3 rounded-lg shadow-sm transition-all focus:ring-2 focus:ring-primary-500/20"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Drivers Table */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h3 className="card-title">Pending Drivers ({pagination.totalRecords || 0})</h3>
        </div>
        <div className="card-body">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="loading-spinner"></div>
              <p className="mt-3 text-sm text-gray-500 font-medium">Loading pending drivers...</p>
            </div>
          ) : drivers.length === 0 ? (
            <div className="empty-state py-12 text-center">
              <Users className="empty-state-icon mx-auto h-12 w-12 text-gray-400 mb-3" />
              <h3 className="empty-state-title text-base font-medium text-gray-900">No pending drivers found</h3>
              <p className="empty-state-description text-sm text-gray-500 mt-1">There are no drivers waiting for approval matching your criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr className="bg-gray-50 text-left">
                    <th className="table-header-cell">Driver Info</th>
                    <th className="table-header-cell">Contact</th>
                    <th className="table-header-cell">License No</th>
                    <th className="table-header-cell">Gender</th>
                    <th className="table-header-cell">Vehicle Details</th>
                    <th className="table-header-cell">Document Status</th>
                    <th className="table-header-cell">Registration Date</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {drivers.map((driver) => {
                    const missingFields = getIncompleteDetails(driver);
                    const isComplete = driver.allDocumentsComplete || missingFields.length === 0;
                    return (
                      <tr key={driver._id} className="table-row hover:bg-gray-50/50">
                        <td className="table-cell">
                          <div className="flex items-center space-x-3">
                            <div className="avatar">
                              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                                <span className="text-primary-700 font-semibold text-sm">
                                  {driver.name?.charAt(0)?.toUpperCase() || 'D'}
                                </span>
                              </div>
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 text-sm">{driver.name || 'N/A'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="table-cell">
                          <div className="text-sm font-medium text-gray-900">{driver.phone || 'N/A'}</div>
                        </td>
                        <td className="table-cell">
                          <div className="text-sm text-gray-900">{driver.licenseNo || 'N/A'}</div>
                        </td>
                        <td className="table-cell">
                          <div className="text-sm text-gray-900 capitalize">{driver.gender || 'N/A'}</div>
                        </td>
                        <td className="table-cell">
                          {driver.vehicleId ? (
                            <div className="text-sm">
                              <div className="flex items-center font-medium text-gray-900">
                                <Car className="h-4 w-4 mr-1 text-gray-400" />
                                {driver.vehicleId.regNo || 'N/A'}
                              </div>
                              <div className="text-xs text-gray-500">
                                {driver.vehicleId.make} {driver.vehicleId.model}
                              </div>
                            </div>
                          ) : (
                            <span className="badge badge-warning">No Vehicle</span>
                          )}
                        </td>
                        <td className="table-cell">
                          {isComplete ? (
                            <span className="badge badge-success inline-flex items-center">
                              <CheckCircle className="h-3.5 w-3.5 mr-1" />
                              Complete
                            </span>
                          ) : (
                            <span
                              className="badge badge-warning cursor-help inline-flex items-center transition-transform hover:scale-105"
                              onMouseEnter={(e) => handleMouseEnterTooltip(e, missingFields)}
                              onMouseLeave={handleMouseLeaveTooltip}
                            >
                              <AlertCircle className="h-3.5 w-3.5 mr-1" />
                              Incomplete ({missingFields.length})
                            </span>
                          )}
                        </td>
                        <td className="table-cell">
                          <div className="text-sm text-gray-900">
                            {driver.createdOn ? new Date(driver.createdOn).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                          </div>
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center space-x-2">
                            <Link
                              to={`/drivers/${driver._id}?from=pending`}
                              className="btn btn-sm btn-outline"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </Link>
                            {hasPermission('drivers', 'write') && (
                              <>
                                <button
                                  onClick={() => handleApproveDriver(driver._id, driver.name)}
                                  className="btn btn-sm btn-success"
                                  title="Approve Driver"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedDriver(driver);
                                    setShowBlockModal(true);
                                  }}
                                  className="btn btn-sm btn-danger"
                                  title="Reject Driver"
                                >
                                  <XCircle className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.total > 1 && (
          <div className="card-footer px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{((pagination.current - 1) * filters.limit) + 1}</span> to{' '}
              <span className="font-medium">{Math.min(pagination.current * filters.limit, pagination.totalRecords)}</span> of{' '}
              <span className="font-medium">{pagination.totalRecords}</span> pending drivers
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(pagination.current - 1)}
                disabled={pagination.current === 1}
                className="btn btn-outline btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600 px-2">
                Page {pagination.current} of {pagination.total}
              </span>
              <button
                onClick={() => handlePageChange(pagination.current + 1)}
                disabled={pagination.current === pagination.total}
                className="btn btn-outline btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Approval Confirmation Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <UserCheck className="h-6 w-6 text-green-600 mr-2" />
              <h3 className="text-lg font-semibold">Approve Driver</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to approve <span className="font-semibold">{driverToApprove?.name}</span>?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelApproval}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={confirmApproval}
                className="btn btn-success"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Block/Reject Modal */}
      {showBlockModal && selectedDriver && (
        <BlockDriverModal
          driver={selectedDriver}
          onClose={() => {
            setShowBlockModal(false);
            setSelectedDriver(null);
          }}
          onBlock={handleBlockDriver}
          onReject={handleRejectDriver}
        />
      )}
      {/* Floating Viewport Tooltip for Incomplete Documents */}
      {activeTooltip && (
        <div
          className="fixed z-50 w-72 p-3.5 bg-gray-900/95 text-white text-xs rounded-xl shadow-2xl border border-gray-700/80 backdrop-blur-sm pointer-events-none"
          style={{
            top: `${activeTooltip.y}px`,
            left: `${Math.max(16, Math.min(activeTooltip.x, window.innerWidth - 304))}px`,
            transform: activeTooltip.placeAbove ? 'translateY(-100%)' : 'none'
          }}
        >
          <div className="font-semibold text-gray-100 mb-2 border-b border-gray-700/80 pb-1.5 flex items-center justify-between">
            <span className="flex items-center gap-1.5">
              <AlertCircle className="h-4 w-4 text-amber-400" />
              Missing Requirements
            </span>
            <span className="bg-amber-400/20 text-amber-300 font-bold px-1.5 py-0.5 rounded text-[10px]">
              {activeTooltip.missingFields.length}
            </span>
          </div>
          <ul className="space-y-1 list-disc list-inside text-gray-300 max-h-52 overflow-y-auto leading-relaxed">
            {activeTooltip.missingFields.map((field, idx) => (
              <li key={idx} className="text-gray-200">
                <span className="font-medium text-amber-300">{field}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PendingDrivers;
